<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { mdiAccountPlus } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import type { Snippet } from "svelte"
  import { createPropertyCustomer } from "../../../../../../../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import CustomerAsyncMultiSelect from "../../../../../../../../_shared/_modules/customer-async-multi-select"
  import {
    defaultOutreachEnabledForRole,
    normalizePropertyCustomerRole,
    propertyCustomerRoleOptions,
  } from "../../../../../../../../../../../../_shared/_logic/customer-roles"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    property: Property
    unit: {
      id?: string | null
      unit_label?: string | null
      label?: string | null
    }
    customers?: Customer[]
    propertyLinks?: PropertyCustomer[]
    onLinked?: (links: PropertyCustomer[]) => void
  }

  let {
    organizationId,
    property,
    unit,
    customers = [],
    propertyLinks = [],
    onLinked = () => {},
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let customerIds = $state<string[]>([])
  let role = $state<PropertyCustomerRole>("OTHER")
  let submitting = $state(false)

  const existingCustomerIds = $derived(
    Array.from(
      new Set(
        (propertyLinks ?? [])
          .filter((link) => link.property_id === property.id && link.property_unit_id === unit.id)
          .map((link) => link.customer_id)
          .filter(Boolean),
      ),
    ),
  )

  const close = () => {
    if (submitting) return

    onremoveOverlay?.()
  }

  const submit = async () => {
    if (submitting) return

    if (!unit.id) {
      newNotification({ class: "error", html: "Bitte zuerst eine gespeicherte Einheit auswählen." })

      return
    }

    const selectedIds = Array.from(
      new Set((customerIds ?? []).map((id) => `${id ?? ""}`.trim()).filter(Boolean)),
    ).filter((id) => !existingCustomerIds.includes(id))

    if (!selectedIds.length) {
      newNotification({
        class: "error",
        html: "Bitte mindestens einen neuen Ansprechpartner auswählen.",
      })

      return
    }

    submitting = true
    try {
      const created: PropertyCustomer[] = []
      for (const customerId of selectedIds) {
        const normalizedRole = normalizePropertyCustomerRole(role)
        const link = await createPropertyCustomer({
          property_id: property.id,
          property_unit_id: unit.id ?? "",
          customer_id: customerId,
          role: normalizedRole,
          outreach_enabled: defaultOutreachEnabledForRole(normalizedRole),
          is_primary: false,
        })
        created.push(link)
      }
      onLinked?.(created)
      newNotification({ class: "success", html: "Ansprechpartner wurden zugeordnet." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("unit_contact_assignment_failed", err)
      newNotification({ class: "error", html: "Ansprechpartner konnten nicht zugeordnet werden." })
    } finally {
      submitting = false
    }
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="assignment-overlay form light">
  <CustomerAsyncMultiSelect
    {organizationId}
    seedCustomers={customers}
    excludeCustomerIds={existingCustomerIds}
    placeholder="Ansprechpartner auswählen"
    bind:value={customerIds}
    brightBackground={true}
    disabled={submitting}
  />

  <Input
    type="select"
    placeholder="Rolle für diese Einheit"
    options={propertyCustomerRoleOptions}
    bind:value={role}
    brightBackground={true}
    disabled={submitting}
  />
</div>

{#snippet actionButtons()}
  <CTA type="secondaryButton" content="Abbrechen" onClick={close} disabled={submitting} />
  <CTA
    type="primaryButton"
    content={submitting ? "Ordne zu…" : "Zuordnen"}
    onClick={submit}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiAccountPlus} />
    {/snippet}
  </CTA>
{/snippet}

<style lang="less">
  .assignment-overlay {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.2rem;

    :global(label),
    :global(.fieldShell),
    :global(.select-input-shell) {
      width: 100%;
      max-width: 100%;
    }
  }
</style>
